import { createSelector } from "reselect";
import lodash from "lodash";
import * as purchaseModes from "../constants/purchaseModes";
import * as entityTypes from "../entities/constants/entityTypes";

import * as priceOfferHelpers from "../helpers/priceOffer";
import { PurchaseModeOffer } from "../../summary/PurchaseMode/helpers/PurchaseModeOffer";

// To allow the history navigation (redux-undo), we need to have one more level: state.present
export const getMainState = (state) => state.present;
export const getUiState = (state) => getMainState(state).ui;

// Current* => offer that is shown on the screen whether selected by user por by default
// To know what the user has selected, go to userSelection selectors
export const getCurrentOfferId = (state) =>
	getUiState(state).currentOffer.offerId;
export const getCurrentOfferState = (state) => getUiState(state).currentOffer;
export const getCurrentProductId = (state) =>
	getUiState(state).currentOffer.productId;
export const getCurrentPriceOfferId = (state) =>
	getUiState(state).currentOffer.priceOfferId;
export const getCurrentPurchaseMode = (state) =>
	getUiState(state).currentOffer.purchaseMode;
export const getEntities = (state) => getMainState(state).entities;
export const getResources = (state) => getMainState(state).resources;
export const getSettings = (state) => getMainState(state).settings;
export const getErrors = (state) => getMainState(state).errors;

export const getEntityState = (entityType) => (state) =>
	getEntities(state)[entityType];
export const getEntityIds = (entityType, idsKey = "allIds") => (state) =>
	getEntities(state)[entityType][idsKey];
export const getEntity = (entityType, id) => (state) =>
	getEntityState(entityType)(state).byId[id];
export const getEntityArray = (entityType, idsKey = "allIds") =>
	createSelector(
		[getEntityState(entityType), getEntityIds(entityType, idsKey)],
		(entities, ids) => {
			return ids.map((id, i) => entities.byId[id]);
		}
	);

export const getOffers = (state) => getEntityState(entityTypes.OFFERS)(state);
export const getPriceOffers = (state) =>
	getEntityState(entityTypes.PRICE_OFFERS)(state);
export const getPurchaseModeOffers = (state) =>
	getEntityState(entityTypes.PURCHASE_MODE_OFFERS)(state);
export const getColors = (state) => getEntityState(entityTypes.COLORS)(state);
export const getProductDetails = (state) =>
	getEntityState(entityTypes.PRODUCT_DETAIL)(state);
export const getMedia = (state) => getEntityState(entityTypes.MEDIA)(state);

export const getAvailableColorIds = createSelector(
	[getCurrentProductId, getProductDetails],
	(productId, productDetails) => {
		return productId ? productDetails.byId[productId].offers : null;
	}
);

export const getNotifications = (state) => getMainState(state).ui.notifications;

export const getCurrentOffer = createSelector(
	[getCurrentOfferId, getOffers],
	(currentOfferId, offers) => {
		return currentOfferId ? offers.byId[currentOfferId] : null;
	}
);

export const getCurrentPriceOffer = createSelector(
	[getCurrentPriceOfferId, getPriceOffers],
	(priceOfferId, priceOffers) => {
		return priceOfferId ? priceOffers.byId[priceOfferId] : null;
	}
);

export const getCurrentOfferHasStock = createSelector(
	[getCurrentOffer, getPurchaseModeOffers, getCurrentPurchaseMode],
	(offer, purchaseModeOffers, purchaseMode) => {
		if (offer && purchaseMode) {
			const purchaseModeOffer = PurchaseModeOffer.getPurchaseModeOffer(
				purchaseMode,
				offer.purchaseModeOffers,
				purchaseModeOffers
			);

			if (purchaseModeOffer && purchaseModeOffer.priceOffers.length > 0) {
				return purchaseModeOffer ? purchaseModeOffer.hasStock : null;
			}
			return null;
		}
		return null;
	}
);

export const getCurrentOfferHasPhysicalStock = createSelector(
	[getCurrentOffer, getPurchaseModeOffers, getCurrentPurchaseMode],
	(offer, purchaseModeOffers, purchaseMode) => {
		if (offer && purchaseMode) {
			const purchaseModeOffer = PurchaseModeOffer.getPurchaseModeOffer(
				purchaseMode,
				offer.purchaseModeOffers,
				purchaseModeOffers
			);

			if (purchaseModeOffer && purchaseModeOffer.priceOffers.length > 0) {
				return offer ? purchaseModeOffer.hasPhysicalStock : null;
			}
			return null;
		}
		return null;
	}
);

export const getPriceOffersForCurrentOfferId = createSelector(
	[getCurrentOffer],
	(offer) => {
		return offer ? offer.purchaseModeOffers : null;
	}
);

export const getPromptPaymentPriceOfferIdForCurrentOffer = createSelector(
	[getPriceOffersForCurrentOfferId, getPurchaseModeOffers],
	(priceOffersOfCurrentOfferIds, purchaseModeOffers) => {
		if (priceOffersOfCurrentOfferIds) {
			const promptPaymentPurchaseModeOffer = PurchaseModeOffer.getPurchaseModeOffer(
				purchaseModes.PROMPT_PAYMENT,
				priceOffersOfCurrentOfferIds,
				purchaseModeOffers
			);
			if (promptPaymentPurchaseModeOffer) {
				const purchaseModeOffer = promptPaymentPurchaseModeOffer;
				return purchaseModeOffer &&
					purchaseModeOffer.priceOffers &&
					purchaseModeOffer.priceOffers.length > 0
					? purchaseModeOffer.priceOffers[0]
					: null;
			}
			return null;
		}
		return null;
	}
);

export const getPromptPaymentPriceOfferForCurrentOffer = createSelector(
	[getPromptPaymentPriceOfferIdForCurrentOffer, getPriceOffers],
	(promptPaymentPriceOfferId, priceOffers) => {
		if (promptPaymentPriceOfferId) {
			return priceOffers.byId[promptPaymentPriceOfferId];
		}
		return null;
	}
);

export const getCurrentCategory = createSelector(
	[getPromptPaymentPriceOfferForCurrentOffer],
	(priceOffer) => {
		return priceOffer ? priceOffer.category : null;
	}
);

export const getCurrentColor = createSelector(
	[getColors, getCurrentOfferId],
	(colors, id) => {
		return colors && id ? colors.byId[id] : null;
	}
);

export const getAvailableColors = createSelector(
	[getColors, getAvailableColorIds],
	(colors, availableColorIds) => {
		let availableColors = null;
		if (availableColorIds && availableColorIds.length > 0) {
			availableColors = [];
			for (const id of availableColorIds) {
				availableColors.push(lodash.merge({ id }, colors.byId[id]));
			}
		}
		return availableColors;
	}
);

export const getCurrentPriceOffersTree = createSelector(
	[getCurrentOffer, getPurchaseModeOffers],
	(selectedOffer, purchaseModeOffers) => {
		let priceOffersTree = null;
		if (selectedOffer && selectedOffer.purchaseModeOffers) {
			const purchaseModeGrouping = PurchaseModeOffer.getPurchaseModeGrouping();
			const purchaseGroups = Object.keys(purchaseModeGrouping);

			priceOffersTree = {};
			for (const key of purchaseGroups) {
				for (const purchaseModeKey of purchaseModeGrouping[key]) {
					const purchaseModeOffer = PurchaseModeOffer.getPurchaseModeOffer(
						purchaseModeKey,
						selectedOffer.purchaseModeOffers,
						purchaseModeOffers
					);

					let selectedPriceOffers = purchaseModeOffer
						? purchaseModeOffer.priceOffers
						: null;
					selectedPriceOffers =
						selectedPriceOffers && selectedPriceOffers.length > 0
							? selectedPriceOffers
							: null;

					if (!priceOffersTree[key] && selectedPriceOffers) {
						priceOffersTree[key] = {};
					}

					if (selectedPriceOffers) {
						priceOffersTree[key][purchaseModeKey] = selectedPriceOffers;
					}
				}
			}
		}
		return priceOffersTree;
	}
);

export const getDetailOfferIds = (state) => getOffers(state).detailIds;
export const getDetailOffers = createSelector(
	[getDetailOfferIds, getOffers],
	(detailIds, offers) =>
		detailIds && detailIds.length > 0
			? detailIds.map((id, i) => offers.byId[id])
			: null
);

export const getMinPriceOfferByOfferAndGroup = createSelector(
	[getDetailOffers, getPurchaseModeOffers, getPriceOffers],
	(detailOffers, purchaseModeOffers, priceOfferObj) => {
		let priceOffersByPurchaseMode = null;
		if (detailOffers != null) {
			priceOffersByPurchaseMode = {};
			lodash.forEach(detailOffers, (offer) => {
				priceOffersByPurchaseMode[
					offer.sapCode
				] = priceOfferHelpers.getOfferMinPriceOffersByPurchaseMode(
					priceOfferObj,
					purchaseModeOffers,
					offer.purchaseModeOffers
				);
			});
		}

		return priceOffersByPurchaseMode;
	}
);

export const getCurrentProductDetails = createSelector(
	[getCurrentProductId, getProductDetails],
	(productId, productDetails) => {
		return productDetails ? productDetails.byId[productId] : null;
	}
);

export const getLastModifiedDate = createSelector(
	[getCurrentProductDetails],
	(productDetails) => (productDetails ? productDetails.lastModifiedTime : null)
);

export const getAllOffersIdsForCurrentPurchaseMode = createSelector(
	[
		getOffers,
		getAvailableColorIds,
		getCurrentPurchaseMode,
		getPurchaseModeOffers
	],
	(offers, colorIds, purchaseMode, purchaseModeOffers) => {
		const priceOfferIds = [];
		if (colorIds) {
			colorIds.forEach((id, i) => {
				if (id) {
					const offer = offers.byId[id];
					if (offer) {
						const priceOfferId = PurchaseModeOffer.getFirstPriceOfferIdForPurchaseMode(
							purchaseMode,
							offer.purchaseModeOffers,
							purchaseModeOffers
						);
						if (priceOfferId) {
							priceOfferIds.push(priceOfferId);
						}
					}
				}
			});
		}
		return priceOfferIds;
	}
);

export const getCurrentOfferPriceOffersByPurchaseMode = createSelector(
	[getCurrentOffer, getPurchaseModeOffers, getPriceOffers],
	(offer, purchaseModeOffers, priceOffersStore) => {
		return offer
			? priceOfferHelpers.getOfferPriceOffersByPurchaseMode(
					priceOffersStore,
					purchaseModeOffers,
					offer.purchaseModeOffers
			  )
			: null;
	}
);

export const isSinglePriceOfferForCurrentPurchaseMode = createSelector(
	[getCurrentOfferPriceOffersByPurchaseMode, getCurrentPurchaseMode],
	(priceOffersByPurchaseMode, purchaseMode) => {
		if (priceOffersByPurchaseMode) {
			if (purchaseMode) {
				const purchaseModeOfferId = PurchaseModeOffer.findMatchingPurchaseModeOfferId(
					purchaseMode,
					Object.keys(priceOffersByPurchaseMode)
				);
				const priceOffers = priceOffersByPurchaseMode[purchaseModeOfferId];
				return priceOffers ? priceOffers.length == 1 : null;
			}
		}
		return null;
	}
);

export const getCurrentMoreInfo = createSelector(
	[getCurrentOfferPriceOffersByPurchaseMode, getCurrentPurchaseMode],
	(priceOffersByPurchaseMode, purchaseMode) => {
		if (PurchaseModeOffer && priceOffersByPurchaseMode) {
			const purchaseModeOfferId = PurchaseModeOffer.findMatchingPurchaseModeOfferId(
				purchaseMode,
				Object.keys(priceOffersByPurchaseMode)
			);
			const priceOffers = priceOffersByPurchaseMode[purchaseModeOfferId];
			return priceOffers && priceOffers.length > 0
				? priceOffers[0].additionalInfoOffer
				: null;
		}

		return null;
	}
);

export const getCurrentOfferMedia = createSelector(
	[getCurrentOfferId, getMedia],
	(offerId, mediaStore) => {
		return offerId ? mediaStore.byId[offerId] : null;
	}
);

export const getTitle = createSelector(
	[getCurrentProductId, getProductDetails],
	(productId, productDetails) => {
		return productId ? productDetails.byId[productId].title : null;
	}
);

export const getImage = createSelector(
	[getCurrentOfferMedia],
	(media) => {
		return media ? media.images[0] : null;
	}
);

export const getCurrentOfferGalleryImages = createSelector(
	[getCurrentOfferMedia],
	(media) => {
		return media ? lodash.concat([], media.images) : null;
	}
);

export const getCurrentOfferPromoImageMediaAndUrl = createSelector(
	[getCurrentOfferMedia],
	(media) => {
		return media
			? { promoImages: media.promoImages, promoImageUrl: media.promoImageUrl }
			: null;
	}
);

export const getColorIdByName = (colorName) =>
	createSelector(
		[getAvailableColors],
		(availableColors) => {
			if (availableColors && availableColors.length > 0) {
				const filteredColors = availableColors.filter(
					(color) => lodash.toLower(color.name) == lodash.toLower(colorName)
				);
				const color =
					filteredColors && filteredColors.length > 0 ? filteredColors[0] : null;
				if (color) {
					return color.id;
				}
			}
			return null;
		}
	);

export const getDefaultPriceOfferForPurchaseMode = createSelector(
	[
		getCurrentOffer,
		getCurrentPurchaseMode,
		getPriceOffers,
		getPurchaseModeOffers,
		getPriceOffersForCurrentOfferId,
		getPromptPaymentPriceOfferForCurrentOffer
	],
	(
		currentOffer,
		currentPurchaseMode,
		priceOffers,
		purchaseModeOffers,
		priceOfferIdsByPurchaseMode
	) => {
		let result = null;
		if (currentOffer) {
			if (priceOfferIdsByPurchaseMode) {
				if (currentPurchaseMode) {
					const offerMinPriceOffersByPurchaseMode = priceOfferHelpers.getOfferMinPriceOffersByPurchaseMode(
						priceOffers,
						purchaseModeOffers,
						priceOfferIdsByPurchaseMode
					);
					if (offerMinPriceOffersByPurchaseMode) {
						const purchaseModeOfferIds = Object.keys(
							offerMinPriceOffersByPurchaseMode
						);

						const purchaseModeOfferId = PurchaseModeOffer.findMatchingPurchaseModeOfferId(
							currentPurchaseMode,
							purchaseModeOfferIds
						);
						if (purchaseModeOfferId) {
							result = offerMinPriceOffersByPurchaseMode[purchaseModeOfferId];
						}
					}
				}
				if ((!result && currentPurchaseMode) || !currentPurchaseMode) {
					// Show the first PurchaseModeOffer we find
					const purchaseModeOfferIds = currentOffer.purchaseModeOffers;
					const purchaseModeOffer = !currentPurchaseMode
						? PurchaseModeOffer.getFirstPurchaseModeOffer(
								purchaseModeOfferIds,
								purchaseModeOffers
						  )
						: PurchaseModeOffer.getNearestFirstPurchaseModeOffer(
								currentPurchaseMode,
								purchaseModeOfferIds,
								purchaseModeOffers
						  );
					if (purchaseModeOffer && purchaseModeOffer.priceOffers) {
						result = priceOffers.byId[purchaseModeOffer.priceOffers[0]];
					}
				}
			}
		}
		return result;
	}
);

export const isSinglePurchaseModeForCurrentPurchaseGroup = createSelector(
	[getCurrentPurchaseMode, getPriceOffersForCurrentOfferId],
	(currentPurchaseMode, priceOfferIdsByPurchaseMode) => {
		const purchaseModeGroup = PurchaseModeOffer.getPurchaseModeGroupByPurchaseMode(
			currentPurchaseMode
		);
		const numberOfValidPurchaseModesForGroup = priceOfferHelpers.getNumberOfValidPurchaseModesForGroup(
			purchaseModeGroup,
			priceOfferIdsByPurchaseMode
		);
		const hasOnlyAValidPurchaseModeForGroup =
			numberOfValidPurchaseModesForGroup == 1;
		return hasOnlyAValidPurchaseModeForGroup;
	}
);

export const getDefaultPriceOfferIdForPurchaseMode = createSelector(
	[getDefaultPriceOfferForPurchaseMode],
	(priceOffer) => {
		return priceOffer ? priceOffer.id : null;
	}
);

export const getOfferIdByPriceOfferId = (priceOfferId) =>
	createSelector(
		[getPurchaseModeOffers, getOffers],
		(purchaseModeOffers, offers) => {
			const keys = lodash.keys(purchaseModeOffers.byId);

			const ownerPurchaseModeOfferId = lodash.find(keys, (purchaseModeOfferId) => {
				const purchaseModeOffer = purchaseModeOffers.byId[purchaseModeOfferId];
				for (const purchaseModeOfferArrayId in purchaseModeOffer.priceOffers) {
					if (
						purchaseModeOffer.priceOffers[purchaseModeOfferArrayId].includes(
							priceOfferId
						)
					) {
						return true;
					}
				}
				return false;
			});
			const offer = lodash.find(offers.byId, (offer) => {
				if (lodash.includes(offer.purchaseModeOffers, ownerPurchaseModeOfferId)) {
					return true;
				}
				return false;
			});
			return offer.sapCode;
		}
	);

export const getProductDetailByOfferId = (offerId) =>
	createSelector(
		[getProductDetails],
		(productDetails) => {
			return lodash.find(
				productDetails.byId,
				(pd) => lodash.findIndex(pd.offers, (id) => id == offerId) >= 0
			);
		}
	);

export const getProductDetailByPriceOfferId = (priceOfferId) => (state) => {
	const offerId = getOfferIdByPriceOfferId(priceOfferId)(state);
	const productDetails = getProductDetailByOfferId(offerId)(state);
	return productDetails;
};

export const isAllSamePriceForCurrentPurchaseMode = createSelector(
	[getCurrentPriceOffersTree, getCurrentPurchaseMode, getPriceOffers],
	(priceOfferTree, purchaseMode, priceOffers) => {
		const purchaseModeGroup = PurchaseModeOffer.getPurchaseModeGroupByPurchaseMode(
			purchaseMode
		);
		if (!purchaseModeGroup || !priceOfferTree || !priceOffers) {
			return false;
		}
		const offersIds = lodash.flatMap(priceOfferTree[purchaseModeGroup]);
		const allPriceOffers = lodash.map(offersIds, (id) => priceOffers.byId[id]);
		return priceOfferHelpers.isAllOffersHaveSamePrice(
			allPriceOffers,
			purchaseModeGroup
		);
	}
);

export const getPromptPaymentPriceOfferIdByOfferId = (offerId) =>
	createSelector(
		[getOffers, getPurchaseModeOffers, getPriceOffers],
		(offers, purchaseModeOffers, priceOffers) => {
			const offer = offers.byId[offerId];
			let promptPaymentPriceOfferId = null;
			if (offer) {
				const priceOffersByPurchaseMode = priceOfferHelpers.getOfferPriceOffersByPurchaseMode(
					priceOffers,
					purchaseModeOffers,
					offer.purchaseModeOffers
				);
				if (priceOffersByPurchaseMode) {
					const promptPaymentPurchaseModeOfferId = PurchaseModeOffer.findMatchingPurchaseModeOfferId(
						purchaseModes.PROMPT_PAYMENT,
						Object.keys(priceOffersByPurchaseMode)
					);
					if (promptPaymentPurchaseModeOfferId) {
						promptPaymentPriceOfferId =
							priceOffersByPurchaseMode[promptPaymentPurchaseModeOfferId][0].id;
					}
				}
			}
			return promptPaymentPriceOfferId;
		}
	);

export const getPurchaseModeGroup = createSelector(
	[getCurrentPurchaseMode],
	(purchaseMode) => {
		return purchaseMode
			? PurchaseModeOffer.getPurchaseModeGroupByPurchaseMode(purchaseMode)
			: null;
	}
);

export const getCurrentPurchaseModeSapCode = createSelector(
	[getCurrentOffer, getPurchaseModeOffers, getCurrentPurchaseMode],
	(offer, purchaseModeOffers, purchaseMode) => {
		if (offer && purchaseMode) {
			const purchaseModeOffer = PurchaseModeOffer.getPurchaseModeOffer(
				purchaseMode,
				offer.purchaseModeOffers,
				purchaseModeOffers
			);
			return offer && purchaseModeOffer ? purchaseModeOffer.sapCode : null;
		}
		return null;
	}
);
